import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../common/Button';

export interface TutorialStep {
  title: string;
  bullets: string[];
  image?: string; // Path to the actual image (e.g., '/tutorial/hider-1.png')
  imagePlaceholder?: string; // Fallback description if no image
}

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  steps: TutorialStep[];
  tutorialKey: string; // Used to persist completion state
}

/**
 * Reusable tutorial modal with step-by-step screens
 */
export const TutorialModal: React.FC<TutorialModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  steps,
  tutorialKey,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to first step when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      // Mark as completed
      localStorage.setItem(`tutorial_${tutorialKey}_completed`, 'true');
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Mark as completed even when skipping
    localStorage.setItem(`tutorial_${tutorialKey}_completed`, 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 pb-safe-bottom">
      <div className="bg-secondary rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col animate-slide-up">
        {/* Header with skip button */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <span className="text-xs text-text-secondary">
            Step {currentStep + 1} of {steps.length}
          </span>
          <button
            onClick={handleSkip}
            className="flex items-center gap-1 text-sm text-text-secondary hover:text-white transition-colors"
          >
            Skip Tutorial
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Tutorial image */}
          <div className="rounded-xl h-48 flex items-center justify-center mb-6 overflow-hidden bg-background/50 border border-white/10">
            {step.image ? (
              <img 
                src={step.image} 
                alt={step.title}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="text-center p-4">
                <img src="/GPSHuntIcon.png" alt="GPS Hunt" className="w-16 h-16 mx-auto mb-2 object-contain" />
                <p className="text-xs text-text-muted">{step.imagePlaceholder || 'Tutorial image'}</p>
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white mb-4">{step.title}</h2>

          {/* Bullets */}
          <ul className="space-y-3">
            {step.bullets.map((bullet, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                <span className="text-text-secondary">{bullet}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer with navigation */}
        <div className="p-4 border-t border-white/10">
          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep
                    ? 'bg-primary'
                    : index < currentStep
                    ? 'bg-primary/50'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <Button
                variant="secondary"
                onClick={handlePrev}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleNext}
              className={`${isFirstStep ? 'w-full' : 'flex-1'} flex items-center justify-center gap-2`}
            >
              {isLastStep ? (
                "Let's Go!"
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Check if a tutorial has been completed
 */
export function isTutorialCompleted(tutorialKey: string): boolean {
  return localStorage.getItem(`tutorial_${tutorialKey}_completed`) === 'true';
}

/**
 * Reset tutorial completion state (for testing)
 */
export function resetTutorial(tutorialKey: string): void {
  localStorage.removeItem(`tutorial_${tutorialKey}_completed`);
}

export default TutorialModal;
